import React, { useEffect, useState } from 'react';
import {
    Backdrop,
    CircularProgress,
    makeStyles,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { EnchancedModal } from 'src/components/Modals';
import { ModalConfirm } from 'src/components/Conversation/ModalConfirm';
import useDocument from 'src/hooks/useDocument';
import { useWarboxMessage } from 'src/hooks';
import type {
    CatalogueDocument,
    TCatalogueModalProps,
} from 'src/views/OmniChannel/types/catalogue';
import {
    buildCatalogueMessage,
    getCatalogueContentType,
    getCatalogueMediaUrl,
} from 'src/views/OmniChannel/utils/catalogue';
import { DocumentsGridLayout } from '../documents';
import { CatalogueDocumentItem } from './CatalogueDocumentItem';

const useStyles = makeStyles((theme) => ({
    sendingBackdrop: {
        zIndex: theme.zIndex.modal + 2,
        color: theme.palette.common.white,
    },
}));

export const CatalogueModal = ({
    open,
    setOpen,
    conversationId,
    storeId,
    area,
    recipientPhone,
    senderPhone,
    isBlocked = false,
    canSendMessages = true,
}: TCatalogueModalProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { sendTextMessage } = useWarboxMessage();
    const { getDocumentsByCatalogue, documentsByCatalogue, loading } =
        useDocument();
    const [selectedDocument, setSelectedDocument] =
        useState<CatalogueDocument | null>(null);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        if (open && storeId && area) {
            getDocumentsByCatalogue({ store: storeId, area });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, storeId, area]);

    useEffect(() => {
        if (!open) {
            setSelectedDocument(null);
            setOpenConfirm(false);
        }
    }, [open]);

    const documentCount = documentsByCatalogue?.length ?? 0;

    const canSendCatalogue =
        Boolean(
            conversationId &&
            storeId &&
            area &&
            recipientPhone &&
            canSendMessages &&
            !isBlocked
        ) && !isSending;

    const handleModalClose = () => {
        if (isSending) return;
        setOpen(false);
    };

    const handleSelectDocument = (document: CatalogueDocument) => {
        if (isSending) return;

        const message = buildCatalogueMessage(document);
        const mediaUrl = getCatalogueMediaUrl(document.file);

        if (!message && !mediaUrl) {
            enqueueSnackbar(t('Errors.CannotSendWsp'), { variant: 'error' });
            return;
        }

        if (!canSendCatalogue) {
            if (isBlocked) {
                enqueueSnackbar(t('Errors.ConversationBlocked'), {
                    variant: 'error',
                });
                return;
            }

            if (!canSendMessages) {
                enqueueSnackbar(t('Errors.NoActiveConversationChannel'), {
                    variant: 'error',
                });
                return;
            }

            enqueueSnackbar(t('Errors.CannotSendWsp'), { variant: 'error' });
            return;
        }

        setSelectedDocument(document);
        setOpenConfirm(true);
    };

    const handleCloseConfirm = () => {
        if (isSending) return;
        setOpenConfirm(false);
        setSelectedDocument(null);
    };

    const handleSendCatalogue = async () => {
        if (!selectedDocument || !canSendCatalogue) return;

        const message = buildCatalogueMessage(selectedDocument);
        const mediaUrl = getCatalogueMediaUrl(selectedDocument.file);
        const contentType = getCatalogueContentType(selectedDocument.file);

        if (!message && !mediaUrl) {
            enqueueSnackbar(t('Errors.CannotSendWsp'), { variant: 'error' });
            return;
        }

        setIsSending(true);

        try {
            await sendTextMessage({
                conversationId: conversationId!,
                storeId: storeId!,
                area: area!,
                senderPhone,
                recipientPhone: recipientPhone!,
                ...(message && { message }),
                ...(mediaUrl && {
                    media: {
                        url: mediaUrl,
                        ...(contentType && { contentType }),
                    },
                }),
            });

            enqueueSnackbar(t('SnackBar.MessageSent'), { variant: 'success' });
            setOpenConfirm(false);
            setSelectedDocument(null);
            setOpen(false);
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Errors.CannotSendWsp';

            enqueueSnackbar(t(errorMessage), { variant: 'error' });
        } finally {
            setIsSending(false);
        }
    };

    return (
        <>
            <EnchancedModal
                open={open}
                setOpen={setOpen}
                title={t('Chat.DocumentsByCatalogue')}
                maxWidth="md"
                onClose={handleModalClose}
                disableBackdropClick={isSending}
                disableEscapeKeyDown={isSending}
            >
                <DocumentsGridLayout
                    loading={loading}
                    documentCount={documentCount}
                    emptyMessage={t('Buttons.NoDocumentation')}
                >
                    {documentsByCatalogue?.map((document: CatalogueDocument) => (
                        <CatalogueDocumentItem
                            key={document._id || document.id}
                            document={document}
                            onSelect={handleSelectDocument}
                        />
                    ))}
                </DocumentsGridLayout>
            </EnchancedModal>

            <ModalConfirm
                open={openConfirm && !isSending}
                handleSend={handleSendCatalogue}
                handleClose={handleCloseConfirm}
            />

            <Backdrop
                className={classes.sendingBackdrop}
                open={isSending}
                onClick={(event) => event.stopPropagation()}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    );
};
